/**
 * @file DealDocumentsTab.tsx
 * @description Вкладка «Документы» карточки сделки (phase09; phase19.31 —
 * часть 3: те же строки и пилюли статусов, что в реестре документов).
 *
 * Сверху — создание черновика: вид документа (`SelectField`, не нативный
 * список) и «Создать черновик» (право `SHOP_USE_CRM`); после создания —
 * переход в карточку черновика, где его выпускают. Ниже — документы сделки
 * общим списком `DocumentCompactList`; состояния — общими `ListStates`.
 */

import { FilePlus2, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { DocumentCompactList } from "./documents/DocumentCompactList";
import { DEAL_DOCUMENT_KINDS } from "./documents/document-view";
import { useHasPermission } from "../../auth/hooks";
import { Button } from "../ui/button";
import { ListEmpty, ListError, ListSkeleton } from "../ui/ListStates";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  DOCUMENT_KIND_LABEL,
  createDocument,
  listDocuments,
  type DealDto,
  type DocumentDto,
  type DocumentKind,
} from "../../lib/api-crm";

const KIND_OPTIONS = DEAL_DOCUMENT_KINDS.map((k) => ({ value: k, label: DOCUMENT_KIND_LABEL[k] }));

interface Props {
  deal: DealDto;
  onChanged?: () => void;
}

export function DealDocumentsTab({ deal, onChanged }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const base = `/shop/${slug}`;
  const navigate = useNavigate();
  const notify = useNotify();
  const canUse = useHasPermission("SHOP_USE_CRM");

  const [items, setItems] = useState<DocumentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newKind, setNewKind] = useState<DocumentKind>("INVOICE");

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listDocuments({ dealId: deal.id, limit: 100 });
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить документы сделки");
    }
  }, [deal.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onCreate() {
    setCreating(true);
    try {
      const doc = await createDocument({ kind: newKind, dealId: deal.id, customerId: deal.customerId });
      notify.success("Черновик создан");
      onChanged?.();
      navigate(`${base}/crm/documents/${doc.id}`);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось создать документ");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {canUse ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SelectField
            ariaLabel="Вид нового документа"
            value={newKind}
            onChange={(value) => setNewKind(value as DocumentKind)}
            options={KIND_OPTIONS}
            className="w-full sm:w-[280px]"
          />
          <Button variant="outline" onClick={() => void onCreate()} loading={creating}>
            <FilePlus2 className="size-4" aria-hidden />
            Создать черновик
          </Button>
        </div>
      ) : null}

      {error && !items ? (
        <ListError message={error} onRetry={() => void load()} />
      ) : items === null ? (
        <ListSkeleton rows={3} compact />
      ) : items.length === 0 ? (
        <ListEmpty
          filtered={false}
          spec={{
            icon: FileText,
            title: "Документов по сделке пока нет",
            description: canUse ? "Выберите вид и создайте черновик — счёт, КП или договор" : undefined,
          }}
        />
      ) : (
        <DocumentCompactList items={items} base={base} aria-label="Документы сделки" />
      )}
    </div>
  );
}
